import * as React from 'react'
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { Button, Divider, Text } from 'react-native-paper'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { MelddModal } from '../../../@ui/containers/modals'
import Column from '../../../@ui/containers/grid/column'
import Row from '../../../@ui/containers/grid/row'
import { UserLink } from '../../../@ui/typo/user.link'
import { useTheme } from '../../../hooks/theme.hook'
import type { AppNavigation } from '../../../navigation/types'
import { useCouponRedemptionsQuery } from '../../../generated/graphql'
import type { AdminCouponRow } from './coupon.card'

type CouponRedemptionsModalProps = {
  visible: boolean
  coupon: AdminCouponRow | null
  onDismiss: () => void
}

const formatRowDate = (iso: string | Date) =>
  moment(iso).format('DD/MM/YYYY HH:mm')

const EM_DASH = '\u2014'

export function CouponRedemptionsModal({
  visible,
  coupon,
  onDismiss,
}: CouponRedemptionsModalProps) {
  const { t } = useTranslation('admin')
  const theme = useTheme()
  const navigation = useNavigation<AppNavigation>()
  const couponId = coupon?.id ?? null
  const couponCode = coupon?.code ?? ''

  const { data, loading, error } = useCouponRedemptionsQuery({
    variables: { couponId: couponId! },
    skip: !visible || !couponId,
    fetchPolicy: 'cache-and-network',
  })

  const rows = data?.couponRedemptions ?? []

  return (
    <MelddModal
      visible={visible}
      onDismiss={onDismiss}
      title={t('redemptions-title', { code: couponCode })}
      scrollable
      maxHeight="85%"
      body={
        <Column gap={0} style={styles.body}>
          {loading && !data ? (
            <ActivityIndicator style={{ marginVertical: 16 }} />
          ) : null}
          {error ? (
            <Text variant="bodyMedium">{t('redemptions-load-error')}</Text>
          ) : null}
          {!loading && !error && rows.length === 0 ? (
            <Text variant="bodyMedium" style={styles.empty}>
              {t('redemptions-empty')}
            </Text>
          ) : null}
          {rows.length > 0 ? (
            <>
              <Row gap={1} style={styles.tableHead}>
                <Text
                  variant="labelMedium"
                  style={[
                    styles.cell,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {t('redemptions-col-date')}
                </Text>
                <Text
                  variant="labelMedium"
                  style={[
                    styles.cell,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {t('redemptions-col-user')}
                </Text>
                <Text
                  variant="labelMedium"
                  style={[
                    styles.cell,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {t('redemptions-col-node')}
                </Text>
              </Row>
              <Divider style={styles.headRule} />
              {rows.map((item) => (
                <View key={item.billingId}>
                  <Row gap={1} style={styles.tableRow} alignItems="flex-start">
                    <Text
                      variant="bodySmall"
                      numberOfLines={2}
                      style={styles.cell}
                    >
                      {formatRowDate(item.redeemedAt)}
                    </Text>
                    <View style={styles.cell}>
                      <UserLink
                        user={{
                          id: item.user.id,
                          username: item.user.username,
                        }}
                        onPress={() => {
                          onDismiss()
                          navigation.navigate('Profile', {
                            userId: item.user.id,
                          })
                        }}
                      />
                    </View>
                    {item.nodeId ? (
                      <Pressable
                        accessibilityRole="link"
                        accessibilityLabel={t('redemptions-node-a11y', {
                          name:
                            item.nodeLabel?.trim() ||
                            t('redemptions-node-untitled'),
                        })}
                        style={styles.cell}
                        onPress={() => {
                          onDismiss()
                          navigation.navigate('ClusterList', {
                            nodeId: item.nodeId as string,
                          })
                        }}
                      >
                        <Text
                          variant="bodySmall"
                          numberOfLines={2}
                          style={[
                            styles.nodeLink,
                            { color: theme.colors.primary },
                          ]}
                        >
                          {item.nodeLabel?.trim() ||
                            t('redemptions-node-untitled')}
                        </Text>
                      </Pressable>
                    ) : (
                      <Text
                        variant="bodySmall"
                        numberOfLines={2}
                        style={styles.cell}
                      >
                        {item.nodeLabel?.trim() ? item.nodeLabel : EM_DASH}
                      </Text>
                    )}
                  </Row>
                  <Divider style={styles.rowRule} />
                </View>
              ))}
            </>
          ) : null}
        </Column>
      }
      actions={
        <View style={styles.actions}>
          <Button mode="contained" onPress={onDismiss}>
            {t('cancel')}
          </Button>
        </View>
      }
    />
  )
}

const styles = StyleSheet.create({
  body: { width: '100%', minHeight: 80 },
  empty: { marginTop: 8, opacity: 0.8 },
  tableHead: {
    paddingBottom: 8,
    paddingTop: 4,
  },
  headRule: { marginBottom: 4 },
  tableRow: {
    paddingVertical: 10,
  },
  rowRule: { marginVertical: 0 },
  cell: {
    flex: 1,
    minWidth: 0,
  },
  nodeLink: {
    textDecorationLine: 'underline',
    alignSelf: 'flex-start',
  },
  actions: { flexDirection: 'row', justifyContent: 'flex-end' },
})
